<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowNodeCard from './FlowNodeCard.vue';
import {
  getLinearSpineIds,
  getOrderedNodeIds,
  insertMessageBeforeEnd,
  insertNodeAfter,
  isStrictLinearFlow,
  removeNode,
  reorderLinearSpine,
  setNodeType,
  swapWithNextOnSpine,
  swapWithPreviousOnSpine,
} from './flowDefinitionSync';
import { FLOW_BUILDER_ADDABLE_TYPES } from './constants';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  teams: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const def = computed(() => props.modelValue);

const orderedIds = computed(() => getOrderedNodeIds(def.value));

const targetIds = computed(() => Object.keys(def.value?.nodes || {}).sort());

const spineIds = computed(() => getLinearSpineIds(def.value));

const isStrictLinear = computed(() => isStrictLinearFlow(def.value));

const draggableMiddles = ref([]);

watch(
  [def, isStrictLinear],
  () => {
    if (!isStrictLinear.value) {
      draggableMiddles.value = [];
      return;
    }
    const s = getLinearSpineIds(def.value);
    if (s.length < 2) {
      draggableMiddles.value = [];
      return;
    }
    draggableMiddles.value = s.slice(1, -1).map(id => ({ id }));
  },
  { immediate: true, deep: true }
);

const apply = next => {
  emit('update:modelValue', next);
};

const onDragEnd = () => {
  const ids = draggableMiddles.value.map(x => x.id);
  apply(reorderLinearSpine(def.value, ids));
};

const addType = ref(FLOW_BUILDER_ADDABLE_TYPES[0]);
const afterIdForInsert = ref('start');

const addTypeOptions = computed(() => [
  { value: 'message', label: t('FLOWS.NODE_TYPE_MESSAGE') },
  { value: 'wait_input', label: t('FLOWS.NODE_TYPE_WAIT_INPUT') },
  { value: 'branch', label: t('FLOWS.NODE_TYPE_BRANCH') },
  { value: 'add_label', label: t('FLOWS.NODE_TYPE_ADD_LABEL') },
  { value: 'assign_team', label: t('FLOWS.NODE_TYPE_ASSIGN_TEAM') },
  { value: 'whatsapp_template', label: t('FLOWS.NODE_TYPE_WHATSAPP_TEMPLATE') },
]);

const afterIdOptions = computed(() =>
  targetIds.value.filter(id => id !== 'end')
);

watch(
  targetIds,
  ids => {
    if (!ids.includes(afterIdForInsert.value)) {
      afterIdForInsert.value = ids.includes('start') ? 'start' : ids[0];
    }
  },
  { immediate: true }
);

const onUpdateNode = (nodeId, node) => {
  const d = JSON.parse(JSON.stringify(def.value));
  d.nodes[nodeId] = node;
  apply(d);
};

const onChangeType = ({ nodeId, newType }) => {
  apply(setNodeType(def.value, nodeId, newType));
};

const onRemove = nodeId => {
  apply(removeNode(def.value, nodeId, 'end'));
};

const spineIndex = nodeId => spineIds.value.indexOf(String(nodeId));

const canMoveUpFn = nodeId => {
  const i = spineIndex(nodeId);
  return i > 1;
};

const canMoveDownFn = nodeId => {
  const i = spineIndex(nodeId);
  const s = spineIds.value;
  return i >= 1 && i < s.length - 2 && s[i + 1] !== 'end';
};

const onMoveUp = nodeId => {
  apply(swapWithPreviousOnSpine(def.value, nodeId));
};

const onMoveDown = nodeId => {
  apply(swapWithNextOnSpine(def.value, nodeId));
};

const onAddMessage = () => {
  const next = insertMessageBeforeEnd(def.value);
  if (!next) {
    useAlert(t('FLOWS.INSERT_LINEAR_BLOCKED'));
    return;
  }
  apply(next);
};

const onInsertTyped = () => {
  const before = JSON.stringify(def.value);
  const next = insertNodeAfter(
    def.value,
    afterIdForInsert.value,
    addType.value
  );
  if (JSON.stringify(next) === before) {
    useAlert(t('FLOWS.INSERT_AFTER_WAIT_BLOCKED'));
    return;
  }
  apply(next);
};
</script>

<template>
  <div class="macros__nodes flex flex-col gap-4">
    <div class="macro__node">
      <span
        class="rounded-md bg-n-solid-blue py-1 px-1.5 text-sm font-medium leading-none text-n-blue-11"
      >
        {{ $t('FLOWS.BUILDER_START') }}
      </span>
    </div>

    <template v-if="isStrictLinear && spineIds.length">
      <div v-if="def.nodes[spineIds[0]]" class="macro__node">
        <FlowNodeCard
          :node-id="spineIds[0]"
          :node="def.nodes[spineIds[0]]"
          :target-ids="targetIds"
          :teams="teams"
          :labels="labels"
          :can-move-up="canMoveUpFn(spineIds[0])"
          :can-move-down="canMoveDownFn(spineIds[0])"
          @update:node="onUpdateNode(spineIds[0], $event)"
          @change-type="onChangeType"
          @remove="onRemove(spineIds[0])"
          @move-up="onMoveUp(spineIds[0])"
          @move-down="onMoveDown(spineIds[0])"
        />
      </div>
      <Draggable
        :list="draggableMiddles"
        animation="200"
        item-key="id"
        ghost-class="ghost"
        handle=".flow-node-drag"
        tag="div"
        class="macros__nodes-draggable flex flex-col gap-4"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div :key="element.id" class="macro__node">
            <FlowNodeCard
              :node-id="element.id"
              :node="def.nodes[element.id]"
              :target-ids="targetIds"
              :teams="teams"
              :labels="labels"
              :can-move-up="canMoveUpFn(element.id)"
              :can-move-down="canMoveDownFn(element.id)"
              show-drag-handle
              class="macros__node-action"
              @update:node="onUpdateNode(element.id, $event)"
              @change-type="onChangeType"
              @remove="onRemove(element.id)"
              @move-up="onMoveUp(element.id)"
              @move-down="onMoveDown(element.id)"
            />
          </div>
        </template>
      </Draggable>
      <div
        v-if="spineIds[spineIds.length - 1] === 'end' && def.nodes.end"
        class="macro__node"
      >
        <FlowNodeCard
          node-id="end"
          :node="def.nodes.end"
          :target-ids="targetIds"
          :teams="teams"
          :labels="labels"
          :can-move-up="canMoveUpFn('end')"
          :can-move-down="canMoveDownFn('end')"
          @update:node="onUpdateNode('end', $event)"
          @change-type="onChangeType"
          @remove="onRemove('end')"
          @move-up="onMoveUp('end')"
          @move-down="onMoveDown('end')"
        />
      </div>
    </template>
    <template v-else>
      <div v-for="nid in orderedIds" :key="nid" class="macro__node">
        <FlowNodeCard
          :node-id="nid"
          :node="def.nodes[nid]"
          :target-ids="targetIds"
          :teams="teams"
          :labels="labels"
          :can-move-up="canMoveUpFn(nid)"
          :can-move-down="canMoveDownFn(nid)"
          @update:node="onUpdateNode(nid, $event)"
          @change-type="onChangeType"
          @remove="onRemove(nid)"
          @move-up="onMoveUp(nid)"
          @move-down="onMoveDown(nid)"
        />
      </div>
    </template>

    <div
      class="flex flex-col gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-1 p-4"
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ $t('FLOWS.ADD_STEPS_TITLE') }}
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          icon="i-lucide-plus"
          :label="$t('FLOWS.ADD_MESSAGE_BEFORE_END')"
          @click="onAddMessage"
        />
      </div>
      <div class="flex flex-wrap items-end gap-2">
        <div class="min-w-[10rem]">
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="flow-after-id"
          >
            {{ $t('FLOWS.INSERT_AFTER') }}
          </label>
          <select
            id="flow-after-id"
            v-model="afterIdForInsert"
            class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          >
            <option v-for="tid in afterIdOptions" :key="tid" :value="tid">
              {{ tid }}
            </option>
          </select>
        </div>
        <div class="min-w-[10rem]">
          <label
            class="mb-1 block text-label-small text-n-slate-11"
            for="flow-add-type"
          >
            {{ $t('FLOWS.NODE_KIND') }}
          </label>
          <select
            id="flow-add-type"
            v-model="addType"
            class="block w-full h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak focus:outline-n-brand"
          >
            <option
              v-for="opt in addTypeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <Button
          size="sm"
          variant="faded"
          :label="$t('FLOWS.INSERT_TYPED')"
          @click="onInsertTyped"
        />
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('FLOWS.REORDER_HINT') }}
        {{ $t('FLOWS.DRAG_HINT') }}
      </p>
    </div>

    <div class="macro__node">
      <span
        class="rounded-md bg-n-solid-blue py-1 px-1.5 text-sm font-medium leading-none text-n-blue-11"
      >
        {{ $t('FLOWS.BUILDER_END') }}
      </span>
    </div>
  </div>
</template>
